import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AppConfig } from '../config/app.config';
import {
  LoginRequest,
  AuthResponse,
  UserDto,
  User,
  ApiErrorResponse
} from '../models/user.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'shopify_connect_token';
  private readonly AUTH_KEY = 'shopify_connect_auth';
  private readonly CURRENT_USER_KEY = 'shopify_connect_current_user';
  private readonly EXPIRES_AT_KEY = 'shopify_connect_expires_at';
  private readonly PERMISSIONS_KEY = 'shopify_connect_permissions';
  private readonly apiBaseUrl = AppConfig.apiBaseUrl;

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.checkAuthStatus());
  private currentUserSubject = new BehaviorSubject<User | null>(this.getCurrentUserFromStorage());
  private permissionsSubject = new BehaviorSubject<string[]>(this.getPermissionsFromStorage());
  private rolesSubject = new BehaviorSubject<string[]>(this.getRolesFromStorage());

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    // Check token expiration on initialization
    this.checkTokenExpiration();
  }

  get isAuthenticated$(): Observable<boolean> {
    return this.isAuthenticatedSubject.asObservable();
  }

  get isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value && !this.isTokenExpired();
  }

  get currentUser$(): Observable<User | null> {
    return this.currentUserSubject.asObservable();
  }

  get currentUser(): User | null {
    return this.currentUserSubject.value;
  }

  get permissions$(): Observable<string[]> {
    return this.permissionsSubject.asObservable();
  }

  get permissions(): string[] {
    return this.permissionsSubject.value;
  }

  get roles$(): Observable<string[]> {
    return this.rolesSubject.asObservable();
  }

  get roles(): string[] {
    return this.rolesSubject.value;
  }

  /**
   * Login using the /api/auth/login endpoint
   */
  login(email: string, password: string): Observable<AuthResponse> {
    const loginRequest: LoginRequest = { email, password };

    return this.http.post<AuthResponse>(`${this.apiBaseUrl}api/auth/login`, loginRequest).pipe(
      tap((response: AuthResponse) => {
        // Store token and auth data
        this.setAuthState(response);
      }),
      catchError((error: HttpErrorResponse) => {
        // Handle different error status codes
        let errorMessage = 'An error occurred during login.';

        if (error.status === 401) {
          const errorResponse = error.error as ApiErrorResponse;
          errorMessage = errorResponse?.error || 'Invalid email or password.';
        } else if (error.status === 500) {
          const errorResponse = error.error as ApiErrorResponse;
          errorMessage = errorResponse?.error || 'An error occurred during login.';
        }

        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Set authentication state after successful login
   */
  private setAuthState(authResponse: AuthResponse): void {
    // Store token
    localStorage.setItem(this.TOKEN_KEY, authResponse.token);

    // Store expiration time
    if (authResponse.expiresAt) {
      localStorage.setItem(this.EXPIRES_AT_KEY, authResponse.expiresAt);
    }

    // Convert UserDto to User for backward compatibility
    const user: User = {
      id: authResponse.user.id,
      username: authResponse.user.username,
      email: authResponse.user.email,
      isActive: authResponse.user.isActive,
      lastLoginAt: authResponse.user.lastLoginAt,
      createdAt: authResponse.user.createdAt,
      roles: authResponse.user.roles
    };

    const permissions = authResponse.permissions || [];

    // Store user and auth state
    localStorage.setItem(this.CURRENT_USER_KEY, JSON.stringify(user));
    localStorage.setItem(this.AUTH_KEY, 'true');
    localStorage.setItem(this.PERMISSIONS_KEY, JSON.stringify(permissions));

    // Update subjects
    this.isAuthenticatedSubject.next(true);
    this.currentUserSubject.next(user);
    this.permissionsSubject.next(permissions);
    this.rolesSubject.next(authResponse.roles || []);
  }

  /**
   * Logout and clear all auth data
   */
  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.AUTH_KEY);
    localStorage.removeItem(this.CURRENT_USER_KEY);
    localStorage.removeItem(this.EXPIRES_AT_KEY);
    localStorage.removeItem(this.PERMISSIONS_KEY);

    this.isAuthenticatedSubject.next(false);
    this.currentUserSubject.next(null);
    this.permissionsSubject.next([]);
    this.rolesSubject.next([]);

    // Redirect to login
    this.router.navigate(['/login']);
  }

  /**
   * Get stored JWT token
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(): boolean {
    const expiresAt = localStorage.getItem(this.EXPIRES_AT_KEY);
    if (!expiresAt) {
      return true;
    }

    const expirationDate = new Date(expiresAt);
    const now = new Date();

    // Add 1 minute buffer to account for clock skew
    return expirationDate.getTime() <= (now.getTime() + 60000);
  }

  /**
   * Check token expiration and logout if expired
   */
  private checkTokenExpiration(): void {
    if (this.isTokenExpired() && this.isAuthenticatedSubject.value) {
      this.logout();
    }
  }

  /**
   * Check authentication status from storage
   */
  private checkAuthStatus(): boolean {
    const token = localStorage.getItem(this.TOKEN_KEY);
    const authFlag = localStorage.getItem(this.AUTH_KEY);
    return !!(token && authFlag === 'true' && !this.isTokenExpired());
  }

  /**
   * Get current user from storage
   */
  private getCurrentUserFromStorage(): User | null {
    const userJson = localStorage.getItem(this.CURRENT_USER_KEY);
    if (userJson) {
      try {
        return JSON.parse(userJson) as User;
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Get permissions from storage
   */
  private getPermissionsFromStorage(): string[] {
    const permissionsJson = localStorage.getItem(this.PERMISSIONS_KEY);
    if (permissionsJson) {
      try {
        return JSON.parse(permissionsJson) as string[];
      } catch {
        return [];
      }
    }
    return [];
  }

  /**
   * Get roles from storage
   */
  private getRolesFromStorage(): string[] {
    const user = this.getCurrentUserFromStorage();
    return user?.roles || [];
  }

  /**
   * Check if user has a specific permission
   */
  hasPermission(permission: string): boolean {
    const permissions = this.permissions;
    if (!permissions || permissions.length === 0) {
      return false;
    }

    // Check for wildcard permission
    if (permissions.includes('*')) {
      return true;
    }

    return permissions.includes(permission);
  }

  /**
   * Check if user has a specific role
   */
  hasRole(roleName: string): boolean {
    const roles = this.roles;
    if (!roles || roles.length === 0) {
      return false;
    }

    return roles.some(role => role.toUpperCase() === roleName.toUpperCase());
  }

  /**
   * Check if user is admin
   */
  isAdmin(): boolean {
    return this.hasRole('ADMIN');
  }

  /**
   * Get the first available route based on user permissions
   * Returns the first route the user has access to, or '/dashboard' as fallback
   */
  getFirstAvailableRoute(): string {
    // Define routes in priority order
    const routes = [
      { path: '/dashboard', permission: 'DASHBOARD' },
      { path: '/products', permission: 'PRODUCTS' },
      { path: '/orders', permission: 'ORDERS' },
      { path: '/gift-cards', permission: 'GIFT_CARD' },
      { path: '/scheduler', permission: 'SCHEDULER' },
      { path: '/users', permission: 'USER_MANAGEMENT' },
      { path: '/documentation', permission: 'DOCUMENTATION' }
    ];

    // Find first route user has permission for (or no permission requirement)
    for (const route of routes) {
      if (!route.permission) {
        return route.path;
      }
      if (this.hasPermission(route.permission)) {
        return route.path;
      }
    }

    return '/dashboard';
  }
}

